<template>
  <div
    :disabled="!isContentReady"
    :class="[progressToolbarClassName, { '-shift': !isProgressbarCompact }]"
  >
    <Tooltip
      :custom-class="tooltip.customClass"
      :align="tooltip.align"
      :target-element="tooltip.targetElement"
      :x-offset="tooltip.xOffset"
    >
      <span v-html="tooltip.text" />
    </Tooltip>
    <div
      ref="progressToolbar"
      class="progress-toolbar"
      dir="ltr"
      @mousemove="showTooltip"
      @mouseleave="hideTooltip"
    >
      <div
        class="extended-view"
        :class="{
          '-hidden': !progressToolbarState.showCollapse || isProgressbarCompact
        }"
      >
        <div v-if="lastBookmarks.length" class="bookmarks-container">
          <div
            v-for="bookmark in lastBookmarks"
            :key="bookmark.id"
            class="bookmarks-icon-container"
            :style="{
              width: `${bookmark.position}%`
            }"
            @click="goToBookmark(bookmark)"
          >
            <BaseSpriteIcon icon-name="ico-bookmark-solid" />
          </div>
        </div>
        <div class="progress-line-wrapper">
          <div
            ref="sliderContainer"
            class="slider-container"
            @click="scrollToClosestPara"
          >
            <div
              ref="slider"
              class="touch-catcher"
              :style="{ left: currentPosition }"
              @mousedown="slideStart"
            >
              <span class="current-slider-position" />
            </div>
            <span
              v-for="(range, index) in progressRanges"
              :key="index"
              class="progress-range"
              :style="{ left: range.left, right: range.right }"
            />
            <span
              v-for="lastPosition in lastReadingPositions"
              :key="lastPosition.key"
              class="last-reading-position"
              :style="{ left: lastPosition.left }"
            />
          </div>
        </div>
      </div>
      <div class="compact-view">
        <div class="side-stats">
          <div v-if="progressToolbarState.showCollapse" class="collapse-block">
            <BaseButton
              :test-id="
                !isProgressbarCompact
                  ? 'book-toolbar collapse-block'
                  : 'book-toolbar expand-block'
              "
              :title="
                isProgressbarCompact
                  ? $t('Accessibility.progressToolbar.show')
                  : $t('Accessibility.progressToolbar.hide')
              "
              color="transparent"
              small
              elevation="0"
              class="icon-wrapper -bigger control-toolbar-button pa-0"
              @click="setExtendedView"
            >
              <BaseSpriteIcon icon-name="ico-arrow-chevron" />
            </BaseButton>

            <div class="progress-toolbar-divider"></div>
          </div>
          <div :style="{ maxWidth: controlsBlockWidth }" class="controls-block">
            <BaseButton
              v-if="progressToolbarState.showReadingSettings"
              color="transparent"
              small
              :title="$t('Accessibility.progressToolbar.settings')"
              elevation="0"
              :class="{ '-active': isReadingSettingsTabOpen }"
              class="pa-0"
              test-id="book-toolbar settings-block"
              @click="toggleReadingSettingsExtrasPopup"
              ><BaseSpriteIcon :icon-name="'ico-dictionary'"
            /></BaseButton>
            <BaseButton
              v-if="progressToolbarState.showNotes"
              color="transparent"
              small
              :title="$t('Accessibility.progressToolbar.notes')"
              elevation="0"
              :class="{ '-active': isNotesTabOpen }"
              class="pa-0"
              test-id="book-toolbar notes-block"
              @click="toggleNotesExtrasPopup"
              ><BaseSpriteIcon :icon-name="'ico-file-edit'"
            /></BaseButton>
            <BaseButton
              v-if="progressToolbarState.showBookmarks"
              color="transparent"
              small
              :title="$t('Accessibility.progressToolbar.bookmarks')"
              elevation="0"
              :class="{ '-active': isBookmarkTabOpen }"
              class="pa-0"
              test-id="book-toolbar bookmark-block"
              @click="processOpenBookmarkPopup"
              ><BaseSpriteIcon :icon-name="'ico-bookmark'"
            /></BaseButton>
            <BaseButton
              color="transparent"
              small
              :title="$t('Accessibility.progressToolbar.toc')"
              elevation="0"
              :class="{ '-active': isTocTabOpen }"
              class="pa-0"
              test-id="book-toolbar toc-block"
              @click="toggleTocExtrasPopup"
              ><BaseSpriteIcon :icon-name="'ico-file-list'"
            /></BaseButton>
            <BaseButton
              v-if="progressToolbarState.showEdit"
              :disabled="!isOnline"
              color="transparent"
              small
              :title="$t('Accessibility.progressToolbar.edit')"
              elevation="0"
              class="pa-0"
              test-id="book-toolbar edit-block"
              @click="setEditMode"
              ><BaseSpriteIcon :icon-name="'ico-edit'"
            /></BaseButton>
            <BaseButton
              v-if="progressToolbarState.showSocialSharing"
              :disabled="!isOnline"
              color="transparent"
              small
              :title="$t('Accessibility.progressToolbar.share')"
              elevation="0"
              :class="{ '-active': isSocialSharingTabOpen }"
              class="pa-0"
              test-id="book-toolbar share-block"
              @click="toggleSocialSharingExtrasPopup"
              ><BaseSpriteIcon :icon-name="'ico-share-icon'"
            /></BaseButton>
          </div>
        </div>
        <div v-if="progressToolbarState.showSelections" class="center-stats">
          <div
            class="chapter-icon -prev"
            test-id="book-toolbar prev-block"
            @click="scrollToChapter(-1)"
          >
            <BaseSpriteIcon icon-name="ico-arrow-chevron" />
          </div>
          <div class="current-para-title">
            <span v-html="chapterTitle"></span>
          </div>
          <div
            class="chapter-icon -next"
            test-id="book-toolbar next-block"
            @click="scrollToChapter(1)"
          >
            <BaseSpriteIcon icon-name="ico-arrow-chevron" />
          </div>
        </div>
        <div class="side-stats -end">
          <div class="progress-wrapper">
            <div
              v-if="progressToolbarState.showReadingStats"
              class="current-progress"
            >
              <span class="progress-percents"
                >{{ currentProgressPercentage }}%</span
              >
            </div>
            <div
              v-if="progressToolbarState.showRemainingReadingTime"
              class="progress-remaining"
            >
              <span>{{
                $t('ProgressToolbar.Remaining', { hours: remainingReadingTime })
              }}</span>
              <span class="progress-remaining-label">{{
                $t('ProgressToolbar.Remaining.label')
              }}</span>
            </div>
          </div>
          <div
            class="progress-toolbar-divider"
            :class="{
              'd-none':
                !progressToolbarState.showReadingStats &&
                !progressToolbarState.showRemainingReadingTime
            }"
          ></div>
          <div
            v-if="progressToolbarState.showReadingStats"
            class="paragraph-progress"
          >
            <span>{{ readToday }}h</span>
          </div>
          <div v-if="progressToolbarState.showDetails">
            {{ currentBookMeta.paragraphsNumber }} selections&nbsp;|&nbsp;
          </div>
          <TimeDuration
            v-if="progressToolbarState.showDetails"
            :duration="bookReadingTime"
            dir="auto"
          ></TimeDuration>
          <BaseButton
            v-if="progressToolbarState.showResetProgress"
            :id="resetProgressId"
            ref="resetProgress"
            color="transparent"
            :title="$t('Accessibility.progressToolbar.reset.tooltip')"
            small
            elevation="0"
            class="icon-wrapper pa-0 -reset-progress-button"
            :class="{ 'no-progress': !progressRanges.length }"
            test-id="book-toolbar reset-progress-block"
            @click="confirmResetProgress"
          >
            <BaseSpriteIcon icon-name="ico-tab-recently" />
          </BaseButton>
        </div>
      </div>
    </div>
    <Popup
      v-if="isConfirmResetShown"
      :content-title="$t('ProgressToolbar.resetReadingStatus.header')"
      :content-message="$t('ProgressToolbar.resetReadingStatus.text')"
      :primary-label="$t('ProgressToolbar.resetReadingStatus.button.cancel')"
      :secondary-label="$t('ProgressToolbar.resetReadingStatus.button.ok')"
      :primary-footer-handler="closeResetPopup"
      :secondary-footer-handler="applyReset"
      :content-image="imageWarn"
      :header-divider="false"
      :footer-divider="false"
      @popupEvent="closeResetPopup"
    ></Popup>
  </div>
</template>

<script>
const CONTROLS_ITEM_WIDTH = 32;
const CONTROLS_ITEM_MARGIN = 8;
const SLIDER_POSITION_SHIFT = '-15px';

import dayJS from '@/dayJS';
import isEmpty from 'lodash/isEmpty';
import Locator from '@shared/publication/locator.mjs';

import Tooltip from '@/components/views/Tooltip/Tooltip.vue';
import TooltipFactory from '@/classes/factories/TooltipFactory';

import LoggerFactory from '@/services/utils/LoggerFactory';
import GoogleAnalyticsUtils from '@/services/utils/GoogleAnalyticsUtils';
import FeatureDetectorService from '@/services/FeatureDetector/FeatureDetectorService';
import PublicationNavigateLogicService from '@/services/PublicationLogic/PublicationNavigateLogicService';
import ProgressToolbarEvents from '@/enums/ProgressToolbarEvents';
import ExtrasComponentsNames from '@/enums/ExtrasComponentsNamesEnum';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon';
import BaseButton from '@/components/base/BaseButton/BaseButton';
import PopupNamesEnum from '@/enums/PopupNamesEnum.js';
import AppConstantsUtil from '@/services/utils/AppConstantsUtil';
import SelectionConstantUtils from '@shared/publication/selection/SelectionConstantUtils.mjs';
import MarkerUtils from '@shared/publication/dom-utils/marker-utils.mjs';
const { getCoordinatesFromEvent, isElementInEventPath } = MarkerUtils;

import { mapGetters } from 'vuex';

import Popup from '@/components/base/Popup/Popup.vue';
import TimeDuration from '@/components/base/TimeDuration/TimeDuration';
import PublicationsTypesEnum from '@shared/enums/PublicationsTypesEnum';
import findLastIndex from 'lodash/findLastIndex';

import images from '@/assets/images';
import Utils from '@/services/utils/Utils';

const logger = LoggerFactory.getLogger('ProgressToolbar.vue');
const resetProgressId = 'resetProgressId';

export default {
  components: {
    TimeDuration,
    BaseSpriteIcon,
    BaseButton,
    Popup,
    Tooltip
  },
  props: {
    bookId: {
      type: String,
      required: true
    },
    materialsId: {
      type: String,
      required: true
    },
    publicationType: {
      type: String,
      required: true
    }
  },
  data() {
    const MAX_DISPLAY_BOOKMARKS_NUM = 3;
    const BOOKMARK_HALF_WIDTH = 6;
    const tooltip = TooltipFactory.createEmptyTopControllTooltip();
    const isBookOpen = this.publicationType === PublicationsTypesEnum.BOOK;
    const isCompilationOpen =
      this.publicationType === PublicationsTypesEnum.COMPILATION;

    return {
      progressToolbarClassName: AppConstantsUtil.PROGRESS_TOOLBAR_CLASS_NAME,
      MAX_DISPLAY_BOOKMARKS_NUM,
      BOOKMARK_HALF_WIDTH,
      tooltip,
      resetProgressId,
      currentProgressPercentage: 0,
      readToday: '0:00',
      remainingReadingTime: '',
      chapterTitle: '',
      lastReadingPositions: [],
      progressRanges: [],
      chapterNumber: 0,
      currentPosition: SLIDER_POSITION_SHIFT,
      tapAreaWidth: 15,
      isSlideInProgress: false,
      READING_AREA_UPDATE_ACTION: 'ProgressStore/callReadingAreaUpdate',
      containerRect: {},
      isConfirmResetShown: false,
      hideToolbarOnScroll: true,
      sliderChanged: false,
      isBookOpen,
      isCompilationOpen
    };
  },
  computed: {
    ...mapGetters({
      isContentReady: 'PublicationStore/isContentReady',
      isCompactData: 'PublicationStore/isCompactData'
    }),
    openParaId() {
      return this.$store.getters['OpenParameterStore/getParaId'];
    },
    isGuest() {
      return this.$store.getters['UserStore/isGuestUser'];
    },
    isOnline() {
      return this.$store.getters['ContextStore/isOnline'];
    },
    readingArea() {
      return this.$store.getters['ProgressStore/getReadingArea'];
    },
    paraIndexesMap() {
      return Object.keys(this.paragraphsSummary);
    },
    isResetProgressSupported() {
      return this.$store.getters['ContextStore/isResetProgressSupported'];
    },
    narrow() {
      return this.$store.getters['MediaDetectorStore/mediaSize'].narrow;
    },
    isScrolling() {
      return this.$store.getters['ContextStore/getIsScrolling'];
    },
    isProgressbarCompact() {
      return this.$store.getters['ContextStore/getIsProgressbarCompact'];
    },
    currentBookMeta() {
      return this.$store.getters['PublicationStore/getMeta'](this.bookId);
    },
    toc() {
      return this.bookView.toc;
    },
    bookReadingTime() {
      return this.currentBookMeta?.readingTime ?? 0;
    },
    isSettingsOpen() {
      return this.$store.getters['ManagePopupStore/isPopupOpened'](
        PopupNamesEnum.READING_SETTINGS_BOTTOM_POPUP
      );
    },
    extrasContext() {
      return this.$store.getters['ManagePopupStore/getPopupContext'](
        PopupNamesEnum.EXTRAS_POPUP
      );
    },
    isReadingSettingsTabOpen() {
      return (
        this.extrasContext?.extrasComponentName ===
        ExtrasComponentsNames.READING_SETTINGS
      );
    },
    isNotesTabOpen() {
      return (
        this.extrasContext?.extrasComponentName === ExtrasComponentsNames.NOTES
      );
    },
    isBookmarkTabOpen() {
      return (
        this.extrasContext?.extrasComponentName ===
        ExtrasComponentsNames.BOOKMARKS
      );
    },
    isTocTabOpen() {
      return (
        this.extrasContext?.extrasComponentName ===
        ExtrasComponentsNames.TABLE_OF_CONTENTS
      );
    },
    isSocialSharingTabOpen() {
      return (
        this.extrasContext?.extrasComponentName ===
        ExtrasComponentsNames.SOCIAL_SHARING
      );
    },
    isExtrasPopupOpen() {
      return this.$store.getters['ManagePopupStore/isPopupOpened'](
        PopupNamesEnum.EXTRAS_POPUP
      );
    },
    isBookmarkPopupOpen() {
      return this.$store.getters['ManagePopupStore/isPopupOpened'](
        PopupNamesEnum.BOOKMARK_POPUP
      );
    },
    isTocOpen() {
      return this.$store.getters['ManagePopupStore/isPopupOpened'](
        PopupNamesEnum.TABLE_OF_CONTENTS_POPUP
      );
    },
    isSocialSharingOpen() {
      return this.$store.getters['ManagePopupStore/isPopupOpened'](
        PopupNamesEnum.SOCIAL_SHARING_POPUP
      );
    },
    bookView() {
      return this.$store.getters['PublicationStore/getBookInfoView']();
    },
    isCompilationEditMode() {
      return this.$store.state.CompilationsStore.isEditMode;
    },
    selectedLocator() {
      return this.$store.getters['MaterialsStore/getSelectedLocator'];
    },
    imageWarn() {
      return images.picWarn;
    },
    lastParaId() {
      return this.$store.getters['PublicationStore/getLastContentParagraph'](
        this.bookId
      );
    },
    progressToolbarState() {
      const showReadingStats = this.isBookOpen;
      const showForAllBookTypes = this.isBookOpen || this.isCompilationOpen;

      return {
        showResetProgress: this.isBookOpen && this.isResetProgressSupported,
        showCollapse: this.isBookOpen,
        showReadingSettings: showForAllBookTypes,
        showNotes: this.isBookOpen,
        showTOC: showForAllBookTypes,
        showBookmarks: this.isBookOpen,
        showSelections: !this.narrow && showForAllBookTypes && this.toc?.length,
        showSocialSharing: showForAllBookTypes,
        showEdit: this.isCompilationOpen,
        showReadingStats,
        showRemainingReadingTime:
          showReadingStats && !this.narrow && this.remainingReadingTime,
        showDetails: this.isCompilationOpen
      };
    },
    bookmarks() {
      return this.$store.getters['BookmarkStore/getBookBookmarks']({
        bookId: this.bookId
      });
    },
    markSpaces() {
      return this.bookmarks.map(mark => {
        const endParaId = mark?.locator?.endLocator?.prefixedParagraphId;
        const bookmarkParaInfo = this.paragraphsSummary
          ? this.paragraphsSummary[endParaId]
          : null;
        return bookmarkParaInfo?.position || 0;
      });
    },
    lastBookmarks() {
      const bookmarks = this.bookmarks;
      bookmarks.sort((a, b) => b.createdAt - a.createdAt);
      if (bookmarks.length > this.MAX_DISPLAY_BOOKMARKS_NUM) {
        bookmarks.length = this.MAX_DISPLAY_BOOKMARKS_NUM;
      }
      bookmarks.sort(
        (a, b) =>
          +a.locator.startLocator._paragraphNumber -
          +b.locator.startLocator._paragraphNumber
      );

      const spaces = this.markSpaces;
      bookmarks.forEach((mark, index) => {
        let curPosition = spaces[index];
        const prevPosition = spaces[index - 1];
        if (prevPosition) {
          curPosition -= prevPosition;
        }
        mark.position = curPosition;
      });

      return bookmarks;
    },
    visibleButtonsCount() {
      const {
        showReadingSettings,
        showNotes,
        showBookmarks,
        showEdit,
        showSocialSharing
      } = this.progressToolbarState;

      const states = [
        showReadingSettings,
        showNotes,
        showBookmarks,
        showEdit,
        showSocialSharing,
        true /* TOC button is always visible */
      ];

      return states.filter(Boolean).length;
    },
    controlsBlockWidth() {
      return (
        this.visibleButtonsCount * CONTROLS_ITEM_WIDTH +
        (this.visibleButtonsCount - 1) * CONTROLS_ITEM_MARGIN +
        'px'
      );
    },
    isMetaBlockInViewPort() {
      return this.$store.getters['ProgressStore/isMetaBlockInViewPort'];
    }
  },
  watch: {
    readingArea(newReadingArea, oldReadingArea) {
      if (
        !newReadingArea ||
        (newReadingArea.startLocator.prefixedParagraphId ===
          oldReadingArea?.startLocator.prefixedParagraphId &&
          newReadingArea.endLocator.prefixedParagraphId ===
            oldReadingArea?.endLocator.prefixedParagraphId)
      ) {
        return;
      }
      this.fillToolbarData();
    },
    isProgressbarCompact(isCompact) {
      this.$_updateIsProgressbarCompact(isCompact);
    },
    isCompilationEditMode(newVal, oldVal) {
      if (oldVal) {
        const forceUpdatePubData = true;
        this.fillToolbarData(forceUpdatePubData);
      }
    },
    isScrolling(val) {
      if (this.sliderChanged) {
        this.sliderChanged = false;
        return;
      }

      if (val && !this.isProgressbarCompact) {
        this.$_updateIsProgressbarCompact(true);
      }
    },
    isMetaBlockInViewPort(newValue) {
      if (newValue) {
        const forceParaNumber = 1;
        this.fillToolbarData(false, forceParaNumber);
      }
    }
  },
  async mounted() {
    await this.fillToolbarData();
    this.$_addTouchEventListener();
    this.$_updateIsProgressbarCompact(true);
  },
  destroyed() {
    this.$_removeTouchEventListener();
    this.$_updateIsProgressbarCompact(true);
  },
  methods: {
    getCurrentMiddleParaNumber() {
      if (!this.readingArea?.startLocator) {
        return null;
      }
      const middleParaIdNum = this.$store.getters[
        'PublicationStore/getMiddleParaIdNum'
      ](this.readingArea.startLocator, this.readingArea.endLocator);
      const isBookEnd =
        this.lastParaId === this.readingArea.endLocator.prefixedParagraphId;
      return isBookEnd
        ? this.readingArea.endLocator._paragraphNumber
        : middleParaIdNum;
    },
    setExtraContentFields() {
      this.paragraphsSummary = this.$store.getters[
        'ProgressStore/getExtraContentFields'
      ]({
        publicationId: this.bookId
      });
    },
    async showTooltip(event) {
      if (this.isProgressbarCompact) {
        return;
      }
      const { hasTouchEvents } = await FeatureDetectorService.getDetections();
      const showResetButtonTooltip = isElementInEventPath(
        this.resetProgressId,
        event
      );
      if (showResetButtonTooltip && !hasTouchEvents) {
        this.$_showResetButtonTooltip();
        return;
      }
      const { clientX } = getCoordinatesFromEvent(event);
      const position = this.$_coordinateToPosition(clientX);
      if (position < 0 || position > 100) {
        this.tooltip.setElement(null);
        return;
      }
      const closestPara = this.getClosestParaId(position);
      const paraMeta = this.paragraphsSummary[closestPara];
      if (!paraMeta || !paraMeta.chapterName) {
        this.tooltip.setElement(null);
        return;
      }
      const tooltipText = this.formatParaTooltip(paraMeta);

      const target = event.currentTarget
        ? event.currentTarget.querySelector('.extended-view')
        : null;
      if (target) {
        this.tooltip.setElement(target);
        this.tooltip.setTooltipText(tooltipText);
        this.tooltip.setXOffset(clientX);
      }
    },
    $_showResetButtonTooltip() {
      const resetButtonElement = this.$refs.resetProgress;
      if (!(resetButtonElement instanceof HTMLElement)) {
        return;
      }
      const resetButtonRect = resetButtonElement.getBoundingClientRect();
      const xOffset = (resetButtonRect.left + resetButtonRect.right) / 2;
      this.tooltip.setElement(resetButtonElement);
      this.tooltip.setTooltipText(
        this.$t('ProgressToolbar.ResetButton.Tooltip')
      );
      this.tooltip.setXOffset(xOffset);
    },
    formatParaTooltip(paraMeta) {
      const paraNum =
        `${paraMeta.paraNum.substr(0, paraMeta.paraNum.indexOf('.') + 1)}` ||
        '';
      return `${paraMeta.paraNum} ${paraMeta.chapterName.replace(paraNum, '')}`;
    },
    hideTooltip() {
      this.tooltip.setElement(null);
    },
    scrollToPara(paragraphId) {
      PublicationNavigateLogicService.openPublication(
        this.$store,
        this.$router,
        {
          paragraphId
        }
      );
    },
    reopenOnResetProgress(paragraphId) {
      PublicationNavigateLogicService.reopenOnResetProgress(
        this.$store,
        this.$router,
        {
          paragraphId
        }
      );
    },
    scrollToChapter(shift) {
      try {
        const currentPara = this.readingArea.startLocator.prefixedParagraphId;
        const currParaNumber = parseInt(MarkerUtils.getParaNum(currentPara));
        let isFound = false;
        let currentChapterIndex = 0;
        for (let i = this.toc.length - 1; i >= 0; i--) {
          let chapterParaNumber = parseInt(
            MarkerUtils.getParaNum(this.toc[i].id)
          );
          if (currParaNumber >= chapterParaNumber) {
            currentChapterIndex = i;
            isFound = true;
            break;
          }
        }
        const paraForScroll = this.toc[
          Math.max(currentChapterIndex + shift, 0)
        ];
        if (paraForScroll && isFound) {
          this.scrollToPara(paraForScroll.id);
        }
      } catch (error) {
        logger.error(
          `Could not navigate to next chapter ${JSON.stringify(error)}`
        );
      }
    },
    async resetProgress() {
      GoogleAnalyticsUtils.trackEvent('book', 'resetProgress');
      const firstParaId = Object.keys(this.paragraphsSummary)[0];
      await this.$store.dispatch('ProgressStore/clearPublicationProgress', {
        bookId: this.bookId,
        type: this.publicationType
      });
      this.reopenOnResetProgress(firstParaId);
      const forceParaNumber = 1;
      this.fillToolbarData(false, forceParaNumber);
      this.$store.dispatch('ProgressStore/clearReadingArea');
      await this.$store.dispatch('ProgressStore/updateReadingArea', {
        readingArea: this.readingArea,
        publicationId: this.bookId,
        type: this.publicationType
      });
    },
    isCursorInsideSliderContainer(cursorClientX) {
      //TODO: add rtl case
      return (
        cursorClientX > this.containerRect.x &&
        cursorClientX < this.containerRect.x + this.containerRect.width
      );
    },
    slideStart() {
      if (process.server) {
        return;
      }
      this.containerRect = this.$refs.sliderContainer.getBoundingClientRect();

      window.addEventListener('mousemove', this.slide);
      window.addEventListener('touchmove', this.slide);
      window.addEventListener('mouseup', this.slideEnd);
      window.addEventListener('touchend', this.slideEnd);
    },
    slide(e) {
      const { clientX } = getCoordinatesFromEvent(e);
      if (!this.isCursorInsideSliderContainer(clientX)) {
        return;
      }
      const nextSliderPosition = this.$_calcPositionInSlider(clientX);
      this.currentPosition = nextSliderPosition;
    },
    $_addTouchEventListener() {
      if (process.server) {
        return;
      }
      if (!this.$refs.progressToolbar) {
        return;
      }

      this.$refs.progressToolbar.addEventListener(
        'touchmove',
        this.showTooltip,
        { passive: true }
      );
      this.$refs.progressToolbar.addEventListener('touchend', this.hideTooltip);

      if (!this.$refs.sliderContainer) {
        return;
      }
      this.$refs.sliderContainer.addEventListener(
        'touchstart',
        this.slideStart,
        { passive: true }
      );
    },
    $_removeTouchEventListener() {
      if (!this.$refs.progressToolbar) {
        return;
      }
      this.$refs.progressToolbar.removeEventListener(
        'touchmove',
        this.showTooltip
      );
      this.$refs.progressToolbar.removeEventListener(
        'touchend',
        this.hideTooltip
      );

      if (!this.$refs.sliderContainer) {
        return;
      }
      this.$refs.sliderContainer.removeEventListener(
        'touchstart',
        this.slideStart
      );
    },
    slideEnd(e) {
      e.preventDefault();
      this.scrollToClosestPara(e);
      window.removeEventListener('mousemove', this.slide);
      window.removeEventListener('touchmove', this.slide);
      window.removeEventListener('mouseup', this.slideEnd);
      window.removeEventListener('touchend', this.slideEnd);
    },
    getClosestParaId(position) {
      const maxIndex = this.currentBookMeta.paragraphsNumber - 1;
      let startArrPosition = Math.floor(
        this.currentBookMeta.paragraphsNumber * (position / 100)
      );
      if (startArrPosition > maxIndex) {
        startArrPosition = maxIndex;
      }
      const closestParaIndex = this.$_closest(position, startArrPosition);
      return this.paraIndexesMap[closestParaIndex];
    },
    scrollToClosestPara(e) {
      e.preventDefault();
      this.sliderChanged = true;
      const { clientX } = getCoordinatesFromEvent(e);
      const position = this.$_coordinateToPosition(clientX, true);
      const closestPara = this.getClosestParaId(position);
      this.scrollToPara(closestPara);
    },
    setExtendedView() {
      this.$store.dispatch('PlaybackStore/pause');
      this.$_updateIsProgressbarCompact(!this.isProgressbarCompact);
      this.$_setProgressRanges();
      this.lastReadingPositions = this.$_getLastReadingPositions();
    },
    confirmResetProgress(event) {
      event.preventDefault();
      this.$store.dispatch('PlaybackStore/pause');
      this.isConfirmResetShown = true;
    },
    async applyReset() {
      try {
        await this.resetProgress();
        this.$_updateIsProgressbarCompact(true);
        this.closeResetPopup();
      } catch (error) {
        logger.error(`Reset progress error: ${error}`);
      }
    },
    closeResetPopup() {
      this.isConfirmResetShown = false;
    },
    async fillToolbarData(forceUpdatePubData, forceParaNumber) {
      if (process.server || this.isCompactData || this.isCompilationEditMode) {
        return;
      }
      try {
        if (
          !this.paragraphsSummary ||
          isEmpty(this.paragraphsSummary) ||
          forceUpdatePubData
        ) {
          await this.$store.dispatch('ProgressStore/initExtraContentFields', {
            publicationId: this.bookId,
            type: this.publicationType
          });
          this.setExtraContentFields();
        }

        const currentMiddleParaNumber = this.getCurrentMiddleParaNumber();
        if (!currentMiddleParaNumber) {
          return;
        }

        const endParaId = this.readingArea.endLocator.prefixedParagraphId;
        const isParaOutEndContent = this.$store.getters[
          'BookStore/isParaOutEndContent'
        ](endParaId);
        const currMiddleParaId = `para_${forceParaNumber ||
          currentMiddleParaNumber}`;
        const currMiddlePara = this.paragraphsSummary[currMiddleParaId];
        if (!currMiddlePara) {
          return;
        }
        this.currentProgressPercentage =
          isParaOutEndContent && !forceParaNumber
            ? 100
            : Math.floor(currMiddlePara.position);
        const paraPositionFixed = isParaOutEndContent
          ? 100
          : currMiddlePara.position;
        const currentPosition = `calc(${paraPositionFixed}% - ${this.tapAreaWidth}px)`;
        this.currentPosition = currentPosition;
        this.$_calculateRemainingTime();
        this.$_calculateReadingTime();
        const middleParaElement = document.getElementById(currMiddleParaId);
        if (!middleParaElement) {
          return;
        }
        this.fillCurrentChapterData(middleParaElement);
      } catch (error) {
        this.$_handleError(error);
      }
    },
    fillCurrentChapterData(middleParaElement) {
      const { chapterTitle, chapterIndex } =
        this.publicationType === PublicationsTypesEnum.COMPILATION
          ? this.$_getCompilationCurrentChapterData()
          : this.$_getBookCurrentChapterData(middleParaElement);
      this.chapterTitle = chapterTitle;
      this.chapterIndex = chapterIndex;
    },
    $_getBookCurrentChapterData(middleParaElement) {
      const middleParaId = middleParaElement.getAttribute('id');
      const middleParaNum = MarkerUtils.getParaNum(middleParaId);

      const nextChapterIndex = this.toc.findIndex(tocItem => {
        const tocParaNum = MarkerUtils.getParaNum(tocItem.id);
        return tocParaNum >= middleParaNum;
      });

      const topParaNum = this.readingArea?.startLocator?._paragraphNumber;
      const topParaChapterIndex = this.toc.findLastIndex(tocItem => {
        const tocParaNum = MarkerUtils.getParaNum(tocItem.id);
        return tocParaNum <= topParaNum;
      });

      const tocChapterIndex =
        topParaChapterIndex > -1 ? topParaChapterIndex : nextChapterIndex - 1;
      const chapterElement = this.toc[tocChapterIndex] || { text: '' };
      return {
        chapterTitle: chapterElement.text,
        chapterIndex: tocChapterIndex + 1
      };
    },
    $_getCompilationCurrentChapterData() {
      const topParaId = this.readingArea.startLocator.paragraphId;
      const tocChapterIndex = findLastIndex(this.bookView.toc, content => {
        return (
          MarkerUtils.getParaNum(content.id) <=
          MarkerUtils.getParaNum(topParaId)
        );
      });
      const chapterElement = this.toc[tocChapterIndex] || { text: '' };
      return {
        chapterTitle: chapterElement.text,
        chapterIndex: tocChapterIndex > -1 ? tocChapterIndex : 0
      };
    },
    $_handleError(error) {
      logger.fatal(`Error in Progress toolbar: ${error.stack}`);
      this.$emit('progressToolbarEvent', {
        type: ProgressToolbarEvents.ERROR,
        data: { error }
      });
    },
    $_closest(position, startArrPosition) {
      const min = 0;
      const max = this.currentBookMeta.paragraphsNumber;
      let index = startArrPosition || 0;
      const current = this.paragraphsSummary[this.paraIndexesMap[index]]
        .position;
      const inverse = current > position;

      for (let i = index; i >= min && i <= max; i += inverse ? -1 : 1) {
        const para = this.paragraphsSummary[this.paraIndexesMap[i]];
        const nextPara = this.paragraphsSummary[this.paraIndexesMap[i + 1]];
        if (
          para &&
          para.position <= position &&
          nextPara &&
          nextPara.position > position
        ) {
          index = i;
          break;
        }
      }

      return index;
    },
    $_updateIsProgressbarCompact(isCompact) {
      this.$store.commit('ContextStore/setIsProgressbarCompact', isCompact);
    },
    $_getLastReadingPositions() {
      return this.$store.getters['ProgressStore/getPreviousPositions']({
        publicationId: this.bookId
      });
    },
    $_setProgressRanges() {
      const ranges = this.$store.getters[
        'ProgressStore/getMergedCurrentRanges'
      ]({ publicationId: this.bookId, type: this.publicationType });
      this.progressRanges = ranges.map(rangeObj => {
        const range = rangeObj.range;
        const startParaId = range.startLocator.prefixedParagraphId;
        const endParaId = range.endLocator.prefixedParagraphId;
        const startPosition = this.paragraphsSummary[startParaId]?.position;
        const ebdPosition = this.paragraphsSummary[endParaId]?.position;
        const isPositionSame = startPosition === ebdPosition;
        const leftOffset = isPositionSame ? startPosition - 0.1 : startPosition;
        const rightOffset = isPositionSame ? startPosition + 0.1 : ebdPosition;
        const rangeSegment = {
          left: leftOffset + '%',
          right: 100 - rightOffset + '%'
        };
        return rangeSegment;
      });
    },
    $_calculateRemainingTime() {
      const remainingTime =
        this.bookReadingTime -
        (this.bookReadingTime * this.currentProgressPercentage) / 100;
      this.remainingReadingTime = dayJS
        .getDuration(remainingTime)
        .format('H:mm');
    },
    $_calculateReadingTime() {
      this.readToday = Utils.getFormattedMinutes(
        this.$store.getters['ProgressStore/getTodayReadWords']({
          publicationId: this.bookId,
          type: this.publicationType
        }) / AppConstantsUtil.DEFAULT_AUDIO_SPEED
      );
    },
    $_coordinateToPosition(mouseCoordX, shouldLimitValue = false) {
      const sliderContainerRect = this.$refs.sliderContainer.getBoundingClientRect();
      const coordX = mouseCoordX - sliderContainerRect.left;
      const position = (coordX / sliderContainerRect.width) * 100;
      if (!shouldLimitValue) {
        return position;
      }
      return position > 100 ? 100 : position < 0 ? 0 : position;
    },
    $_calcPositionInSlider(clientX) {
      let position =
        ((clientX - this.containerRect.x) / this.containerRect.width) * 100;
      position = position > 100 ? 100 : position < 0 ? 0 : position;
      return `calc(${position}% - ${this.tapAreaWidth}px)`;
    },
    toggleReadingSettingsExtrasPopup() {
      try {
        this.$store.dispatch('PlaybackStore/pause');
        if (this.isReadingSettingsTabOpen) {
          this.$store.dispatch('ManagePopupStore/closePopup', {
            name: PopupNamesEnum.EXTRAS_POPUP
          });
          return;
        }

        const popupAction = this.isExtrasPopupOpen
          ? 'ManagePopupStore/updatePopupContext'
          : 'ManagePopupStore/openPopup';

        this.$store.dispatch(popupAction, {
          name: PopupNamesEnum.EXTRAS_POPUP,
          popupContext: {
            extrasComponentName: ExtrasComponentsNames.READING_SETTINGS,
            extrasHeaderTitle: this.parseEnumValue(
              ExtrasComponentsNames.READING_SETTINGS
            )
          }
        });
      } catch (err) {
        this.errorHandler(err);
      }
    },
    toggleNotesExtrasPopup() {
      try {
        this.$store.dispatch('PlaybackStore/pause');
        if (this.isNotesTabOpen) {
          this.$store.dispatch('ManagePopupStore/closePopup', {
            name: PopupNamesEnum.EXTRAS_POPUP
          });
          return;
        }

        const popupAction = this.isExtrasPopupOpen
          ? 'ManagePopupStore/updatePopupContext'
          : 'ManagePopupStore/openPopup';

        this.$store.dispatch(popupAction, {
          name: PopupNamesEnum.EXTRAS_POPUP,
          popupContext: {
            publicationType: this.publicationType,
            bookId: this.bookId,
            materialsId: this.materialsId,
            extrasComponentName: ExtrasComponentsNames.NOTES,
            extrasHeaderTitle: this.parseEnumValue(ExtrasComponentsNames.NOTES)
          }
        });
      } catch (err) {
        this.errorHandler(err);
      }
    },
    toggleBookmarksExtrasPopup() {
      try {
        this.$store.dispatch('PlaybackStore/pause');
        if (this.isBookmarkTabOpen) {
          this.$store.dispatch('ManagePopupStore/closePopup', {
            name: PopupNamesEnum.EXTRAS_POPUP
          });
          return;
        }
        const popupAction = this.isExtrasPopupOpen
          ? 'ManagePopupStore/updatePopupContext'
          : 'ManagePopupStore/openPopup';

        this.$store.dispatch(popupAction, {
          name: PopupNamesEnum.EXTRAS_POPUP,
          popupContext: {
            extrasComponentName: ExtrasComponentsNames.BOOKMARKS,
            extrasHeaderTitle: this.parseEnumValue(
              ExtrasComponentsNames.BOOKMARKS
            )
          }
        });
      } catch (err) {
        this.errorHandler(err);
      }
    },
    toggleTocExtrasPopup() {
      try {
        this.$store.dispatch('PlaybackStore/pause');
        if (this.isTocTabOpen) {
          this.$store.dispatch('ManagePopupStore/closePopup', {
            name: PopupNamesEnum.EXTRAS_POPUP
          });
          return;
        }

        const popupAction = this.isExtrasPopupOpen
          ? 'ManagePopupStore/updatePopupContext'
          : 'ManagePopupStore/openPopup';

        const extrasHeaderTitle = this.isCompilationOpen
          ? 'Sections'
          : this.parseEnumValue(ExtrasComponentsNames.TABLE_OF_CONTENTS);

        this.$store.dispatch(popupAction, {
          name: PopupNamesEnum.EXTRAS_POPUP,
          popupContext: {
            extrasComponentName: ExtrasComponentsNames.TABLE_OF_CONTENTS,
            extrasHeaderTitle
          }
        });
      } catch (err) {
        this.errorHandler(err);
      }
    },
    async toggleSocialSharingExtrasPopup() {
      try {
        this.$store.dispatch('PlaybackStore/pause');
        if (this.isSocialSharingTabOpen) {
          this.$store.dispatch('ManagePopupStore/closePopup', {
            name: PopupNamesEnum.EXTRAS_POPUP
          });
          return;
        }

        const popupAction = this.isExtrasPopupOpen
          ? 'ManagePopupStore/updatePopupContext'
          : 'ManagePopupStore/openPopup';

        const popupContext = this.isCompilationOpen
          ? {
              linkToShare: await this.$store.dispatch(
                'CompilationsStore/share',
                this.bookId
              )
            }
          : { bookId: this.bookId };

        this.$store.dispatch(popupAction, {
          name: PopupNamesEnum.EXTRAS_POPUP,
          popupContext: {
            ...popupContext,
            locator: null,
            extrasComponentName: ExtrasComponentsNames.SOCIAL_SHARING,
            extrasHeaderTitle: this.parseEnumValue(
              ExtrasComponentsNames.SOCIAL_SHARING
            )
          }
        });
      } catch (err) {
        this.errorHandler(err);
      }
    },
    processGuestEvent() {
      if (this.$store.getters['ContextStore/isPurchaseEnabled']) {
        this.$store.dispatch('PaymentsStore/openSubscribePopup');
      } else {
        this.$store.dispatch('ManagePopupStore/openPopup', {
          name: PopupNamesEnum.CONNECT,
          popupContext: {}
        });
      }
    },
    processOpenBookmarkPopup() {
      if (this.isGuest) {
        this.processGuestEvent();
        return;
      }

      this.toggleBookmarksExtrasPopup();
    },
    setEditMode() {
      const isExtrasPopupOpened = this.$store.getters[
        'ManagePopupStore/isPopupOpened'
      ](PopupNamesEnum.EXTRAS_POPUP);
      if (isExtrasPopupOpened) {
        this.$store.dispatch('ManagePopupStore/closePopup', {
          name: PopupNamesEnum.EXTRAS_POPUP
        });
      }
      this.$store.commit('CompilationsStore/setIsEditMode', true);
    },
    goToBookmark(bookmark) {
      const locator = bookmark.locator.startLocator;
      const isInsideReadingArea = this.$store.getters[
        'ProgressStore/isInsideReadingArea'
      ](locator);
      if (!isInsideReadingArea) {
        this.$_changePublication(bookmark.locator);
      }

      this.$store.dispatch('BookmarkStore/setBookmarkId', bookmark.id);

      this.$store.dispatch('ManagePopupStore/closePopup', {
        name: PopupNamesEnum.BOOKMARK_POPUP
      });
    },
    windowHalfHight() {
      const toolbarHeight = SelectionConstantUtils.TOOLBAR_HEIGHT;
      const progressToolbarHeight =
        SelectionConstantUtils.PROGRESS_TOOLBAR_HEIGHT;
      const windowHalfHight =
        window.innerHeight / 2 - toolbarHeight - progressToolbarHeight;
      return -Math.round(windowHalfHight);
    },
    $_changePublication({ startLocator, endLocator }) {
      if (
        !(startLocator instanceof Locator.InTextLocator) ||
        !(endLocator instanceof Locator.InTextLocator)
      ) {
        return;
      }

      try {
        const openLocator = new Locator.InTextRangeLocator(
          startLocator,
          endLocator
        ).toJSON();
        const openSamePara = this.openParaId === openLocator;
        PublicationNavigateLogicService.openPublication(
          this.$store,
          this.$router,
          {
            paragraphId: openLocator,
            openSamePara,
            openOffset: this.windowHalfHight()
          }
        );
      } catch (err) {
        logger.error(
          `Get error while navigate in book while view bookmark (${this.bookId}); error: ${err}`
        );
      }
    },
    parseEnumValue(value) {
      if (!value) {
        return null;
      }

      let result = value
        .replace(/Tab$/, '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
        .toLowerCase();

      result = result.charAt(0).toUpperCase() + result.slice(1);

      return result;
    }
  }
};
</script>
<style scoped lang="less">
@import 'ProgressToolbar.less';
</style>
